import React from "react";
import { motion } from "framer-motion";
import { Check, Plus, Star, Sparkles } from "lucide-react";

const scrollToBooking = () => document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });

const SPECIALS = [
  { title: "10% OFF", text: "Your first detail with us" },
  { title: "Save $25", text: "Book two vehicles together" },
  { title: "Refer a Friend", text: "You both save $15" },
];

export const Services = ({ services, addons = [], onSelect }) => (
  <section id="services" data-testid="services" className="relative overflow-hidden bg-background px-6 py-24 md:px-12 md:py-32">
    <div className="glow-teal left-[-10%] top-1/3 h-[420px] w-[420px] bg-teal-500/20" />
    <div className="glow-teal right-[-8%] bottom-0 h-[380px] w-[380px] bg-violet-500/15" />
    <div className="relative mx-auto max-w-[1500px]">
      <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-primary">/ The Menu</span>
          <h2 className="mt-6 font-cabinet text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
            <span className="gradient-text">Detailing<br />Packages</span>
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed text-zinc-400">
          Fully mobile — we come to your home or work. Every package is a fixed price, no surprises.
          <span className="mt-3 block font-mono-x text-xs uppercase tracking-widest text-primary">Prices for cars & sedans · Vans +$25 · SUVs & Trucks +$50</span>
        </p>
      </div>

      {/* Grand Opening Specials */}
      <div className="mb-12 grid grid-cols-1 gap-px overflow-hidden border border-primary/30 bg-white/10 md:grid-cols-3">
        <div className="flex items-center gap-3 bg-background px-6 py-4 md:col-span-3">
          <Sparkles size={16} className="text-primary" />
          <span className="font-mono-x text-xs uppercase tracking-[0.25em] gradient-text">Grand Opening Specials</span>
        </div>
        {SPECIALS.map((sp) => (
          <div key={sp.title} data-testid={`special-${sp.title}`} className="bg-background px-6 py-5">
            <p className="font-cabinet text-2xl font-black uppercase tracking-tight gradient-text">{sp.title}</p>
            <p className="mt-1 text-sm text-zinc-400">{sp.text}</p>
          </div>
        ))}
      </div>

      {/* Packages */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {services.map((s, i) => (
          <motion.div
            key={s.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`service-${s.slug}`}
            className={`group relative flex flex-col p-8 transition-colors duration-500 ${
              s.popular ? "gradient-fill" : "border border-white/10 hover:border-white/25"
            }`}
          >
            <div className={`flex h-full flex-col ${s.popular ? "bg-background p-7" : ""}`}>
              {s.popular ? (
                <span className="mb-4 inline-flex w-fit items-center gap-1.5 gradient-fill px-3 py-1 font-mono-x text-[10px] uppercase tracking-widest text-black">
                  <Star size={12} /> Most Popular
                </span>
              ) : (
                <span className="mb-4 font-mono-x text-[11px] uppercase tracking-widest text-zinc-500">{s.duration}</span>
              )}

              <h3 className="font-cabinet text-3xl font-black uppercase tracking-tight text-white">{s.name}</h3>
              <div className="mt-3 font-cabinet text-5xl font-black">
                <span className="align-top text-xl text-primary">$</span><span className="gradient-text">{s.price}</span>
                {s.popular ? <span className="ml-2 font-mono-x text-[11px] uppercase tracking-widest text-zinc-500">{s.duration}</span> : null}
              </div>

              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{s.description}</p>

              <ul className="mt-6 space-y-3">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check size={16} className="mt-0.5 shrink-0 text-primary" /> {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`book-${s.slug}`}
                onClick={() => { onSelect?.(s.slug); scrollToBooking(); }}
                className={`mt-8 flex w-full items-center justify-center gap-3 px-6 py-4 font-mono-x text-xs uppercase tracking-widest transition-all duration-300 ${
                  s.popular
                    ? "gradient-fill text-black hover:opacity-90"
                    : "border border-white/20 text-white hover:border-transparent hover:gradient-fill hover:text-black"
                }`}
              >
                <Plus size={16} /> Book {s.name}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add-ons */}
      {addons.length > 0 ? (
        <div className="mt-12 border border-white/10 bg-card p-8 md:p-10">
          <span className="font-mono-x text-xs uppercase tracking-[0.25em] text-primary">/ Add-On Services</span>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {addons.map((a) => (
              <div key={a.value} data-testid={`addon-${a.value}`} className="flex items-center justify-between border-b border-white/10 pb-4">
                <span className="text-base text-white">{a.label}</span>
                <span className="font-mono-x text-sm text-zinc-400">
                  {a.note ? `${a.note} ` : ""}<span className="gradient-text font-bold">${a.price}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  </section>
);
